<section
    class="b2b-dashboard-shell"
    [class.component-dark-theme]="themeService.isDark()"
    [class.rtl-enabled]="themeService.isRTLEnabled()"
>
    <section class="b2b-hero mb-25">
        <div class="b2b-hero__content">
            <div class="b2b-hero__eyebrow">
                <span class="pulse-dot"></span>
                {{ 'b2b_dashboard.hero.eyebrow' | translate }}
            </div>

            <h1>{{ 'b2b_dashboard.hero.title' | translate }}</h1>
            <p>{{ 'pie_dashboard.hero.description' | translate }}</p>

            <div class="b2b-hero__actions">
                <button
                    mat-flat-button
                    type="button"
                    class="hero-primary-btn"
                    (click)="scrollTo('pie-performance-section')"
                >
                    <i class="material-symbols-outlined">monitoring</i>
                    {{ 'b2b_dashboard.actions.view_performance' | translate }}
                </button>
                <button
                    mat-button
                    type="button"
                    class="hero-secondary-btn"
                    (click)="scrollTo('pie-plant-list-section')"
                >
                    <i class="material-symbols-outlined">travel_explore</i>
                    {{ 'b2b_dashboard.actions.inspect_plants' | translate }}
                </button>
            </div>

            <div class="b2b-hero__metrics">
                @for (metric of heroMetrics; track metric.labelKey) {
                    <div class="hero-metric-card">
                        <span>{{ metric.labelKey | translate }}</span>
                        <strong>
                            {{ metric.value }}
                            @if (metric.suffix) {
                                <small>{{ metric.suffix }}</small>
                            }
                        </strong>
                        @if (metric.trendKey) {
                            <em>{{ metric.trendKey | translate:metric.trendParams }}</em>
                        } @else {
                            <em>{{ metric.trend }}</em>
                        }
                    </div>
                }
            </div>
        </div>

        <div class="b2b-hero__visual" aria-hidden="true">
            <div class="orbital-grid"></div>
            <div class="visual-aura visual-aura--one"></div>
            <div class="visual-aura visual-aura--two"></div>
            <div class="visual-ring visual-ring--outer"></div>
            <div class="visual-ring visual-ring--inner"></div>
            <div class="electron-orbit electron-orbit--one"></div>
            <div class="electron-orbit electron-orbit--two"></div>
            <div class="electron-orbit electron-orbit--three"></div>

            <div class="operations-core">
                <span class="operations-core__halo"></span>
                <span class="operations-core__pulse"></span>
                <div class="operations-core__glyph">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>

            <div class="floating-glass-card floating-glass-card--monitor">
                <span>{{ 'pie_dashboard.visual.live_monitoring' | translate }}</span>
                <strong>+12.4%</strong>
                <small>{{ 'pie_dashboard.visual.output_trend' | translate }}</small>
            </div>

            <div class="floating-glass-card floating-glass-card--health">
                <span>{{ 'Fleet_health' | translate }}</span>
                <strong>98.2%</strong>
                <small>{{ 'pie_dashboard.visual.stable_systems' | translate }}</small>
            </div>

            <div class="floating-glass-card floating-glass-card--sync">
                <span>{{ 'pie_dashboard.visual.signal_sync' | translate }}</span>
                <strong>24/7</strong>
                <small>{{ 'pie_dashboard.visual.continuous_watch' | translate }}</small>
            </div>

            <div class="signal-node signal-node--one"></div>
            <div class="signal-node signal-node--two"></div>
            <div class="signal-node signal-node--three"></div>
            <div class="signal-node signal-node--four"></div>
            <div class="signal-node signal-node--five"></div>

            <div class="pulse-track pulse-track--a"></div>
            <div class="pulse-track pulse-track--b"></div>
            <div class="pulse-track pulse-track--c"></div>
            <div class="pulse-track pulse-track--d"></div>
            <div class="energy-lane energy-lane--left"></div>
            <div class="energy-lane energy-lane--right"></div>

            <div class="pulse-wave">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
            </div>
        </div>
    </section>

    <section class="fleet-highlight-grid mb-25">
        @for (item of fleetHighlights; track item.labelKey) {
            <mat-card
                class="daxa-card fleet-highlight-card border-radius bg-white border-none d-block"
                [class.is-success]="item.state === 'success'"
                [class.is-warning]="item.state === 'warning'"
                [class.is-info]="item.state === 'info'"
            >
                <mat-card-content>
                    <div class="fleet-highlight-card__icon">
                        <i class="material-symbols-outlined">{{ item.icon }}</i>
                    </div>
                    <div>
                        <span>{{ item.labelKey | translate }}</span>
                        <strong>{{ item.valueKey | translate }}</strong>
                        <small>{{ item.helperKey | translate:item.helperParams }}</small>
                    </div>
                </mat-card-content>
            </mat-card>
        }
    </section>

    <div id="pie-performance-section">
        <app-overview-table></app-overview-table>
    </div>

    <div class="b2b-main-grid">
        <div class="b2b-main-grid__map">
            <app-plant-map></app-plant-map>
        </div>

        <aside class="b2b-main-grid__side">
            <app-plant-status-graph></app-plant-status-graph>
            <app-savings></app-savings>
        </aside>
    </div>

    <section class="energy-flow-panel mb-25">
        <div class="section-heading">
            <div>
                <span class="section-kicker">{{ 'b2b_dashboard.energy_intelligence.kicker' | translate }}</span>
                <h2>{{ 'b2b_dashboard.energy_intelligence.today_title' | translate }}</h2>
            </div>
            <div class="sync-pill">
                <span class="pulse-dot"></span>
                {{ 'pie_dashboard.energy.live_sync_enabled' | translate }}
            </div>
        </div>

        <div class="energy-flow-grid">
            @for (flow of energyFlow; track flow.titleKey) {
                <div class="energy-flow-card">
                    <div class="energy-flow-card__icon">
                        <i class="material-symbols-outlined">{{ flow.icon }}</i>
                    </div>
                    <div class="energy-flow-card__body">
                        <span>{{ flow.titleKey | translate }}</span>
                        <strong>{{ flow.value }}</strong>
                        <small>{{ flow.metaKey | translate:flow.metaParams }}</small>
                    </div>
                </div>
            }
        </div>
    </section>

    <section class="insight-grid mb-25">
        @for (insight of insights; track insight.titleKey) {
            <article
                class="insight-card"
                [class.is-success]="insight.tone === 'success'"
                [class.is-warning]="insight.tone === 'warning'"
                [class.is-danger]="insight.tone === 'danger'"
                [class.is-info]="insight.tone === 'info'"
            >
                <div class="insight-card__icon">
                    <i class="material-symbols-outlined">{{ insight.icon }}</i>
                </div>
                <div>
                    <h3>{{ insight.titleKey | translate }}</h3>
                    <p>{{ insight.textKey | translate }}</p>
                </div>
            </article>
        }
    </section>

    <div id="pie-plant-list-section">
        <app-plant-list-table></app-plant-list-table>
    </div>
</section>
